import "./MoneyCreditManagementPage.css";

import React, { useEffect, useState } from "react";

import Headers from "Components/Header/Headers";
import Navigation from "Components/SideNavBar/Navigation";
import RenderInformationPopup from "Components/Popup/InformationPopup/PureComponents/InformationPopup";
import StripeOnboardingRequiredPopup from "Components/Popup/StripeOnboardingRequiredPopup/StripeOnboardingRequiredPopup";
import useStripeOnboardingGuard from "Hooks/useStripeOnboardingGuard";
import dollarIcon from "Media/icons/cards-with-dollar-sign@2x.png";
import lifeSaverIcon from "Media/icons/lifesaver@2x.png";
import moneyBack from "Media/icons/moneyBack.png";
import renewIcon from "Media/icons/renew@2x.png";
import { toast } from "react-toastify";
import tradeIcon from "Media/icons/trade@2x.png";
import useIsAmniusOrganization from "Hooks/useIsAmniusOrganization";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import userIcon from "Media/icons/user (1)@2x.png";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import WalletTopUpModal from "Components/Popup/WalletTopUpModal/WalletTopUpModal";
import WalletPayoutModal from "Components/Popup/WalletPayoutModal/WalletPayoutModal";
import { getClientCreditBalance } from "Config/API/get";
import { fetchStripeConnectedAccountId } from "Helper/stripeConnectedAccount";
import {
  getStripeCreditBalanceAction,
  getUsableCreditBalanceAction,
} from "Redux/action/get.action";

const TILE_HEIGHT_STYLE = { height: "155px" };
const TILE_ICON_STYLE = { width: "65px", height: "65px" };
const WALLET_BUTTON_CLASS =
  "rates-and-subscription-lower-tile-button mc-management-wallet-tile__button";

const MoneyCreditManagementPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isOrgAmnius = useIsAmniusOrganization();
  const [showWalletTopup, setShowWalletTopup] = useState(false);
  const [showWalletPayout, setShowWalletPayout] = useState(false);
  const [showStripeOnboardingPopup, setShowStripeOnboardingPopup] =
    useState(false);
  const [infoPopup, setInfoPopup] = useState(false);
  const [tabsText, setTabsText] = useState("");
  const { isOnboardingRequired } = useStripeOnboardingGuard();
  const credit = useSelector((state) => state.getStripeCreditBalanceReducer);
  const dashboardurl = useSelector((state) => state.getUsableCrediturlReducer);
  const loggedInUserDetails = useSelector(
    (state) => state.authenticationReducer
  );
  const generalInfo = useSelector((state) => state.renderGeneralInfo);
  const token = localStorage.getItem("token");
  const [stripeAccountId, setStripeAccountId] = useState(null);

  const canTopUpWallet = loggedInUserDetails?.add_credit_rights === "manager";
  const isCreditAccountManager =
    loggedInUserDetails?.credit_account_manager === "manager";
  const canPayout = isCreditAccountManager;
  const canManageStripe = isCreditAccountManager;
  const canViewCredits = loggedInUserDetails?.credit_read_rights === "manager";
  const hasStripeConnectedId = Boolean(stripeAccountId);
  const hasStripeDashboardUrl = Boolean(
    dashboardurl && String(dashboardurl).trim()
  );
  const canUseWalletActions = hasStripeConnectedId && hasStripeDashboardUrl;

  useEffect(() => {
    let cancelled = false;
    fetchStripeConnectedAccountId(token, loggedInUserDetails, generalInfo).then(
      (accountId) => {
        if (!cancelled) setStripeAccountId(accountId);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [token, loggedInUserDetails, generalInfo]);

  const refreshCreditBalance = async () => {
    const accountId =
      stripeAccountId ||
      (await fetchStripeConnectedAccountId(
        token,
        loggedInUserDetails,
        generalInfo
      ));
    if (!accountId) return;

    const response = await getClientCreditBalance(token, accountId);
    if (response?.data?.status) {
      const creditData = response?.data?.data?.[0] || {};
      dispatch(getStripeCreditBalanceAction(creditData?.amount));
      dispatch(getUsableCreditBalanceAction(creditData?.usableAmount));
    }
  };

  const renderInfoIcon = (
    text,
    { className = "moneyCreditManagement-info", stopPropagation = false } = {}
  ) => (
    <QuestionMarkIcon
      className={className}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        setInfoPopup(true);
        setTabsText(text);
      }}
    />
  );

  // Returns true (and prompts the user) when Stripe onboarding must be completed first.
  const blockUntilStripeOnboarded = (message) => {
    if (!isOnboardingRequired) return false;
    toast(message, { type: "info" });
    setShowStripeOnboardingPopup(true);
    return true;
  };

  const showNoRightsToast = (message) => toast(message, { type: "info" });

  // A tile with an icon, a label and a "?" info icon
  const renderTile = ({ icon, iconStyle, label, onClick, infoText }) => (
    <div className="mc-management-tile">
      <div
        className="moneyCredit-management-tile"
        style={TILE_HEIGHT_STYLE}
        onClick={onClick}
      >
        <img
          src={icon}
          alt="Wallet"
          style={{ ...TILE_ICON_STYLE, ...iconStyle }}
        />
        <span>{label}</span>
      </div>
      {renderInfoIcon(infoText)}
    </div>
  );

  return (
    <>
      <div className="mc-management-main">
        <div className="mc-management-header">
          <Headers />
        </div>

        <div className="mc-management-navigation">
          <Navigation />
        </div>

        <div className="mc-management-wrapper">
          <div className="mc-management-heading">Money Management Page</div>
          <div className="mc-management-content">
            <div className="mc-management-tile mc-management-wallet-tile">
              <img
                className="mc-management-wallet-tile__icon"
                src={dollarIcon}
                alt="Wallet"
              />
              <div className="mc-management-wallet-tile__body">
                <span className="mc-management-wallet-tile__title">
                  Stripe Wallet Available Balance
                </span>
                <span className="mc-management-wallet-tile__balance">
                  {credit || 0}
                </span>
                {(canTopUpWallet || canPayout || canManageStripe) && (
                  <div className="mc-management-wallet-tile__actions">
                    {canTopUpWallet && (
                      <button
                        type="button"
                        className={WALLET_BUTTON_CLASS}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            blockUntilStripeOnboarded(
                              "Complete Stripe onboarding before topping up your wallet."
                            )
                          ) {
                            return;
                          }
                          if (!canUseWalletActions) return;
                          setShowWalletTopup(true);
                        }}
                      >
                        Top Up Wallet
                      </button>
                    )}
                    {canPayout && (
                      <button
                        type="button"
                        className={WALLET_BUTTON_CLASS}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            blockUntilStripeOnboarded(
                              "Complete Stripe onboarding before requesting a payout."
                            )
                          ) {
                            return;
                          }
                          if (!canUseWalletActions) return;
                          setShowWalletPayout(true);
                        }}
                      >
                        Payout
                      </button>
                    )}
                    {canManageStripe && hasStripeDashboardUrl && (
                      <button
                        type="button"
                        className={`${WALLET_BUTTON_CLASS} mc-management-wallet-tile__button--secondary`}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(dashboardurl, "_blank");
                        }}
                      >
                        Manage Stripe Account
                      </button>
                    )}
                  </div>
                )}
              </div>
              {renderInfoIcon("CURRENT CREDITS", { stopPropagation: true })}
            </div>
            <div
              className="mc-management-tile"
              onClick={() => navigate("/rates-and-subscription-management")}
            >
              <img
                className="mc-management-tile__icon"
                src={renewIcon}
                alt="Rates and Subscription"
              />
              <span className="mc-management-tile__label">
                Rates & Subscription
              </span>
              {renderInfoIcon("Rates & Subscription", {
                stopPropagation: true,
              })}
            </div>
            {!isOrgAmnius && (
              <div
                className="mc-management-tile"
                style={TILE_HEIGHT_STYLE}
                onClick={() => {
                  if (canViewCredits) return navigate("/all-refund-requests");
                  showNoRightsToast(
                    "You do not have rights to view refund requests"
                  );
                }}
              >
                <img src={moneyBack} alt="Wallet" style={TILE_ICON_STYLE} />
                <span>Refund Requests</span>
                {renderInfoIcon("Refund Requests", {
                  className: "transfer-and-banking-info",
                })}
              </div>
            )}
            {renderTile({
              icon: userIcon,
              label: "User Management",
              infoText: "User Management",
              onClick: () =>
                navigate("/service-ticket-user-management", {
                  state: { tab: "creditHandling" },
                }),
            })}
            {renderTile({
              icon: tradeIcon,
              label: "Transaction Activity",
              infoText: "Transaction Activity",
              onClick: () => {
                if (canViewCredits) {
                  return navigate("/money-credit-transaction-activity");
                }
                showNoRightsToast(
                  "You do not have rights to view transaction activities"
                );
              },
            })}
            {!isOrgAmnius &&
              renderTile({
                icon: lifeSaverIcon,
                iconStyle: { marginRight: "20px" },
                label: "Contact Support",
                infoText: "Contact Support",
                onClick: () => navigate("/contact-support"),
              })}
          </div>

          {isOrgAmnius && (
            <div
              className="mc-management-lower-tile"
              onClick={() => navigate("/all-refund-requests")}
            >
              <img src={moneyBack} alt="Wallet" style={TILE_ICON_STYLE} />
              <span>Refund Requests</span>
            </div>
          )}
        </div>
      </div>

      {infoPopup && (
        <RenderInformationPopup message={tabsText} hide={setInfoPopup} />
      )}
      {showWalletTopup && canTopUpWallet && (
        <WalletTopUpModal
          hide={setShowWalletTopup}
          token={token}
          dashboardUrl={dashboardurl}
          onSuccess={async () => {
            await refreshCreditBalance();
            setShowWalletTopup(false);
          }}
        />
      )}
      {showWalletPayout && canPayout && (
        <WalletPayoutModal
          hide={setShowWalletPayout}
          token={token}
          accountId={stripeAccountId}
          onSuccess={refreshCreditBalance}
        />
      )}
      {showStripeOnboardingPopup && (
        <StripeOnboardingRequiredPopup
          hide={setShowStripeOnboardingPopup}
          dashboardUrl={dashboardurl}
        />
      )}
    </>
  );
};

export default MoneyCreditManagementPage;
